import { useCallback, useState } from "react"
import { ArrowPathIcon } from "@heroicons/react/24/solid"
import { useAppSelector } from "@/store/hooks"
import { canRequestLandscape, requestLandscape } from "@/Hooks/useFullscreen"
import ModalButton from "@/Components/ModalButton"

// A player who dismissed the gate shouldn't meet it again after a reload or a
// trip back to the lobby, but a fresh tab starts over.
const DISMISSED_KEY = "landscapeOverlayDismissed"

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "true"
  } catch {
    // Private modes can throw on access; the gate simply stays.
    return false
  }
}

// A narrow window is not a phone. Gating on width was survivable while this
// only covered the game screen, but the overlay now sits above every route, and
// someone who merely made a desktop browser tall and thin would meet a
// full-screen "rotate your device" wall on the login page. Pointer coarseness
// asks the question that actually matters: false on desktop at any size, true
// on phones and tablets whatever their resolution.
const isTouchDevice = () => window.matchMedia?.("(pointer: coarse)").matches ?? false

const LandscapeOverlay: React.FC = () => {
  const isPortrait = useAppSelector((state) => state.global.isPortrait)
  // A device does not grow a touchscreen mid-session.
  const [isTouch] = useState(isTouchDevice)

  const [dismissed, setDismissed] = useState(readDismissed)
  // Probed once: whether the browser can rotate the screen itself doesn't
  // change while the page is open.
  const [canRotate] = useState(canRequestLandscape)
  // Set when the browser claimed it could rotate and then didn't, so the
  // player gets the manual instruction instead of a button that did nothing.
  const [rotateFailed, setRotateFailed] = useState(false)

  const handleRotate = useCallback(async () => {
    // Awaiting inside the tap is fine — the gesture is only needed to *start*
    // the fullscreen request, which happens first.
    if (!(await requestLandscape())) setRotateFailed(true)
  }, [])

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, "true")
    } catch {
      // Falling back to component state alone is good enough for this session.
    }
    setDismissed(true)
  }, [])

  if (!isTouch || !isPortrait || dismissed) return null

  const showButton = canRotate && !rotateFailed

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex flex-col items-center justify-center text-white p-4">
      <div className="text-center max-w-md">
        <ArrowPathIcon className="w-16 h-16 mx-auto mb-6 animate-spin" />
        <h2 className="text-5xl font-bold mb-4">
          {showButton ? "Play in Landscape" : "Please Rotate Your Device"}
        </h2>
        <p className="text-2xl">
          {showButton
            ? "This game is best experienced in landscape mode. Tap below and we'll turn the screen for you."
            : "This game is best experienced in landscape mode. Please rotate your device to continue playing."}
        </p>

        {showButton && (
          <div className="mt-8 flex justify-center">
            <ModalButton color="green" onClick={handleRotate}>
              Rotate to Landscape
            </ModalButton>
          </div>
        )}

        {/* Rotation lock keeps the device in portrait however the player holds
            it, so without a way past this screen they'd have no way in. */}
        <button
          onClick={handleDismiss}
          className="mt-8 text-xl text-white/60 underline underline-offset-4 hover:text-white/90"
        >
          Continue in portrait anyway
        </button>
      </div>
    </div>
  )
}

export default LandscapeOverlay
